import { Badge, Grid, Space, Typography, message } from 'antd';
import {
  CloseOutlined,
  DeleteOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import apex from '../../assets/images/apex-legends-icon-16.jpg';
import React, { useEffect, useRef } from 'react';
import classes from './CartIcon.module.scss';
import Headline from '../UI/Headline/Headline';
import Button from '../UI/Button/Button';
import { ROUTES } from '../AppRouter/AppRouter';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { getUser } from '../../redux/selectors';
import { useOutsideClick } from '../../utils/useOutsideClick';
import { updateInCart, getCart } from '../../http/cart';

const CartIcon = ({ cart, onRemove, products, user, cartOpen }) => {
  const { i18n, t } = useTranslation();
  const breakpoints = Grid.useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ref = useRef(null);
  useOutsideClick(ref, () => toggleCart(false));

  const totalPrice = cart.reduce(
    (acc, product) => acc + product.price * product.quantity,
    0
  );
  const totalAmount = cart.reduce(
    (acc, product) => acc + product.quantity,
    0
  );
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (user.id) fetchCart();
  }, []);

  function toggleCart(open) {
    dispatch({ type: 'App/SET_CART_OPEN', payload: open });
  }

  const fetchCart = async () => {
    try {
      await dispatch(getCart());
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit = () => {
    navigate(ROUTES.CART);
    toggleCart(false);
  };

  const incrementQuantity = async (product) => {
    try {
      await dispatch(
        updateInCart(product.id, product.denominationId, product.quantity + 1)
      );
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to update quantity'
      });
    }
  };

  const decrementQuantity = async (product) => {
    try {
      await dispatch(
        updateInCart(product.id, product.denominationId, product.quantity - 1)
      );
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to update quantity'
      });
    }
  };

  return (
    <div
      className={classes.container}
      style={{
        width: breakpoints.lg ? 50 : breakpoints.md ? 46 : 40,
        height: breakpoints.lg ? 50 : breakpoints.md ? 46 : 40
      }}
    >
      {contextHolder}

      {cartOpen && <div className={classes.backdrop}></div>}

      <Badge count={cart.length}>
        <ShoppingCartOutlined
          onClick={() => toggleCart(!cartOpen)}
          style={{ fontSize: 30 }}
        />
      </Badge>
      {cartOpen && (
        <div direction='vertical' size={10} className={classes.cart} ref={ref}>
          <div className={classes.closeIcon} onClick={() => toggleCart(false)}>
            <CloseOutlined style={{ fontSize: 28, color: '#9989C7' }} />
          </div>
          <Headline>{t('cart.title')}</Headline>
          {cart.length !== 0 && (
            <Space
              direction='vertical'
              size={40}
              className={classes.cart__items}
            >
              {cart.map((item) => {
                return (
                  <div>
                    <div className={classes.cart__item} key={item.id}>
                      <div
                        className={classes.cart__img}
                        onClick={() => {
                          toggleCart(false);
                          navigate({
                            pathname: ROUTES.PRODUCT_CARD,
                            search: `?product=${item.productId}`
                          });
                        }}
                      >
                        <img src={item?.imgUrl || apex} alt='' />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 20,
                          justifyContent: 'space-between',
                          flexGrow: 1,
                          paddingInline: 20,
                          flexDirection: breakpoints.xs ? 'column' : 'row'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: 20,
                            justifyContent: 'space-between',
                            flexDirection: 'column'
                          }}
                        >
                          <Space
                            direction='vertical'
                            size={breakpoints.xs ? 10 : 20}
                          >
                            <Typography
                              style={{
                                fontSize: breakpoints.xs ? 18 : 24,
                                lineHeight: '20px',
                                fontWeight: 400,
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                toggleCart(false);
                                navigate({
                                  pathname: ROUTES.PRODUCT_CARD,
                                  search: `?product=${item.productId}`
                                });
                              }}
                            >
                              {i18n.language === 'en'
                                ? item?.name
                                : item?.name_lv}
                            </Typography>
                            <Typography
                              style={{
                                fontSize: breakpoints.xs ? 16 : 20,
                                lineHeight: '20px',
                                fontWeight: 400
                              }}
                            >
                              {t('cart.nominal')}:{' '}
                              {`${i18n.language === 'en' ? item?.denominationName : item?.denominationName_lv} EUR ${item?.price?.toFixed(2)}`}
                            </Typography>
                          </Space>
                          <div
                            style={{
                              display: 'flex',
                              gap: 20,
                              alignItems: 'center'
                            }}
                          >
                            <div
                              className={`${classes.quantityBtn} ${item.quantity === 1 ? classes.disabled : ''}`}
                              onClick={
                                item.quantity === 1
                                  ? null
                                  : () => decrementQuantity(item)
                              }
                            >
                              -
                            </div>
                            <Typography
                              style={{
                                fontWeight: 400,
                                fontSize: breakpoints.xs ? 16 : 20
                              }}
                            >
                              {item.quantity}
                            </Typography>
                            <div
                              className={`${classes.quantityBtn} ${(item.quantity >= item.stock || totalAmount >= 5) ? classes.disabled : ''}`}
                              onClick={
                                item.quantity >= item.stock || totalAmount >= 5
                                  ? null
                                  : () => incrementQuantity(item)
                              }
                            >
                              +
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexShrink: 0,
                            flexDirection: breakpoints.xs ? 'row' : 'column'
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: breakpoints.xs ? 18 : 24,
                              lineHeight: '20px',
                              wordWrap: 'normal',
                              fontWeight: 400
                            }}
                          >
                            EUR {(item?.price * item?.quantity)?.toFixed(2)}
                          </Typography>
                          <DeleteOutlined
                            onClick={() => onRemove(item)}
                            style={{ fontSize: 20, lineHeight: '20px' }}
                          />
                        </div>
                      </div>
                    </div>
                    {item.quantity >= item.stock && (
                      <Typography style={{ color: 'red', fontWeight: 700 }}>
                        {t('pages.productCard.outOfStock')}
                      </Typography>
                    )}
                  </div>
                );
              })}
            </Space>
          )}
          {cart.length === 0 && (
            <Typography
              style={{
                fontSize: 20,
                lineHeight: '20px',
                fontWeight: 400,
                color: '#B8B5B6',
                marginBottom: 40,
                marginTop: 20
              }}
            >
              {t('cart.empty')}
            </Typography>
          )}
          <Button
            disabled={cart.length === 0 || totalAmount > 5 || totalPrice > 1000}
            wide
            onClick={onSubmit}
          >
            {t('cart.checkout')}
          </Button>
          {totalAmount > 5 && (
            <Typography
              style={{
                fontSize: 16,
                lineHeight: '20px',
                fontWeight: 400,
                color: 'red',
                marginTop: 20
              }}
            >
              {t('cart.limit.quantity')}
            </Typography>
          )}
          {totalPrice > 1000 && (
            <Typography
              style={{
                fontSize: 16,
                lineHeight: '20px',
                fontWeight: 400,
                color: 'red',
                marginTop: 20
              }}
            >
              {t('cart.limit.price')}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  products: state.games.products,
  user: getUser(state),
  cartOpen: state.app.cart.open
});

export default connect(mapStateToProps, null)(CartIcon);
